/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * SSR-safe portal wrapper. `createPortal` is not supported by React's server
 * renderer, so during the first render (server + initial hydration) we return
 * null. After mount, the actual portal goes live.
 *
 * Used for menus, dropdowns, and overlay modals — UI that is interactive only,
 * not SEO content, so the post-mount delay is invisible to users.
 */

import type React from 'react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

interface ClientPortalProps {
  children: React.ReactNode;
  /** Defaults to document.body. */
  container?: Element | null;
}

export const ClientPortal: React.FC<ClientPortalProps> = ({ children, container }) => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  if (!mounted) return null;
  const target = container ?? (typeof document !== 'undefined' ? document.body : null);
  if (!target) return null;
  return createPortal(children, target);
};
